import { z } from "zod"

export const templatizationTemplateMetadataSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  category: z.enum(["sales", "finance", "service", "trade-in", "other"]),
  documentKind: z.string().optional(),
  stateCodes: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  compliance: z
    .object({
      esignAllowed: z.boolean(),
      requiresWetSignature: z.boolean().optional(),
      requiresNotary: z.boolean().optional(),
      identityRequirement: z.enum(["none", "provider_attested", "nist_ial2"]).optional(),
      notes: z.string().optional(),
    })
    .optional(),
})

export const templatizationMergeBindingSchema = z.object({
  definitionKey: z.string(),
  primarySource: z.enum([
    "standard",
    "dealCustomField",
    "dealershipCustomField",
    "manual",
    "importColumn",
    "apiProvided",
  ]),
  partySelector: z.string().optional(),
  addressType: z
    .enum(["mailing", "registration", "residence", "business", "billing", "shipping", "physical"])
    .optional(),
  phoneType: z.enum(["mobile", "home", "work", "business", "other"]).optional(),
  requiredAtSend: z.boolean().optional(),
  fallback: z.enum(["blank", "manual"]).optional(),
  defaultValue: z.string().optional(),
})

export const templatizationValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  pattern: z.string().optional(),
  errorMessage: z.string().optional(),
  email: z.boolean().optional(),
  phone: z.boolean().optional(),
  numeric: z.boolean().optional(),
  alphanumeric: z.boolean().optional(),
  minDate: z.string().optional(),
  maxDate: z.string().optional(),
  dateFormat: z.string().optional(),
  minChecked: z.number().optional(),
  maxChecked: z.number().optional(),
})

export const templatizationSelectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
})

export const templatizationCheckboxGroupSchema = z.object({
  groupKey: z.string(),
  optionValue: z.string(),
})

export const templatizationChoiceGroupSchema = z.object({
  groupKey: z.string(),
  groupLabel: z.string().optional(),
  optionValue: z.string().optional(),
  optionLabel: z.string().optional(),
  selectionMode: z.enum(["single", "multi"]),
  valueSource: z.enum(["documentInput", "mergeEnum"]),
})

export const templatizationDraftFieldSchema = z.object({
  sourceFieldName: z.string(),
  candidateFieldType: z.enum(["text", "checkbox", "select", "signature", "date", "initial"]),
  label: z.string(),
  fieldCategory: z.enum(["static", "prefill", "required", "optional"]).optional(),
  recipientAccess: z.enum(["readonly", "required", "optional"]).optional(),
  signTimeRecipientAccess: z.enum(["readonly", "required", "optional"]).optional(),
  collectionPhase: z.enum(["packet_creation", "sign_time"]).optional(),
  valueSource: z.enum(["constant", "merge", "senderInput"]).optional(),
  signerRole: z.enum(["buyer", "cosigner", "dealer"]).optional(),
  signerOrder: z.number().optional(),
  mergeBinding: templatizationMergeBindingSchema.optional(),
  validation: templatizationValidationSchema.optional(),
  defaultValue: z.string().optional(),
  placeholder: z.string().optional(),
  tooltip: z.string().optional(),
  groupName: z.string().optional(),
  checkboxGroup: templatizationCheckboxGroupSchema.optional(),
  choiceGroup: templatizationChoiceGroupSchema.optional(),
  selectOptions: z.array(templatizationSelectOptionSchema).optional(),
  selectValueSource: z.enum(["documentInput", "mergeEnum"]).optional(),
  selectSourceDefinitionKey: z.string().optional(),
  confidence: z.number().min(0).max(1),
  issues: z.array(z.string()).optional(),
  reasoning: z.string().optional(),
})

export const templatizationDraftSchema = z.object({
  template: templatizationTemplateMetadataSchema,
  fields: z.array(templatizationDraftFieldSchema),
  summary: z.object({
    overallConfidence: z.number().min(0).max(1),
    reviewNotes: z.array(z.string()).optional(),
  }),
})

export type TemplatizationDraft = z.infer<typeof templatizationDraftSchema>
export type TemplatizationDraftField = z.infer<typeof templatizationDraftFieldSchema>
